import React, { useState } from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ColumnManagementProps {
    columnKeys: string[];
    visibleColumns: Record<string, boolean>;
    onColumnOrderChange: (newOrder: string[]) => void;
    onColumnToggle: (columnKey: string) => void;
    fixedKeys?: string[];
    labels?: Record<string, string>;
}

const SortableItem = ({
    id,
    isVisible,
    onToggle,
    isDraggable,
    label,
}: {
    id: string;
    isVisible: boolean;
    onToggle: (id: string) => void;
    isDraggable: boolean;
    label: string;
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id,
        disabled: !isDraggable || !isVisible,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || "transform 250ms cubic-bezier(0.25, 1, 0.5, 1)",
        opacity: isDragging ? 0.9 : 1,
        zIndex: isDragging ? 2000 : "auto",
        background: isDragging ? "#fff" : "transparent",
        border: isDragging ? "2px solid #3d5ee1" : "none",
        boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.15)" : "none",
        position: "relative" as "relative",
    };

    const renderStatusIcon = () => {
        if (isDraggable && isVisible) {
            return (
                <div
                    className="me-2 text-muted"
                    style={{ display: "flex", alignItems: "center" }}
                >
                    <i className="ti ti-arrows-move fs-18" />
                </div>
            );
        }
        return (
            <div
                className="me-2 text-muted-light"
                style={{ width: 20, display: "flex", justifyContent: "center" }}
            >
                <i
                    className={!isVisible ? "ti ti-eye-off fs-14" : "ti ti-lock fs-14"}
                    title={!isVisible ? "Column Hidden" : "Column Fixed"}
                />
            </div>
        );
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`d-flex align-items-center justify-content-between mb-2 p-2 rounded`}
        >
            <div
                className="d-flex align-items-center flex-grow-1"
                {...(isDraggable && isVisible ? { ...attributes, ...listeners } : {})}
                style={{
                    cursor: isDraggable && isVisible ? "move" : "default"
                }}
            >
                {renderStatusIcon()}
                <p
                    className={`mb-0 text-capitalize ${!isDraggable ? "text-muted" : ""}`}
                >
                    {label}
                </p>
            </div>
            <div className="status-toggle ms-2">
                <input
                    type="checkbox"
                    className="check"
                    id={`col-toggle-${id}`}
                    checked={isVisible}
                    onChange={() => onToggle(id)}
                />
                <label htmlFor={`col-toggle-${id}`} className="checktoggle" />
            </div>
        </div>
    );
};

const ColumnManagement: React.FC<ColumnManagementProps> = ({
    columnKeys,
    visibleColumns,
    onColumnOrderChange,
    onColumnToggle,
    fixedKeys = [],
    labels = {},
}) => {
    const [searchTerm, setSearchTerm] = useState("");

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = columnKeys.indexOf(active.id as string);
            const newIndex = columnKeys.indexOf(over.id as string);

            const newOrder = arrayMove(columnKeys, oldIndex, newIndex);
            onColumnOrderChange(newOrder);
        }
    };

    const filteredKeys = columnKeys.filter((key) => {
        if (!key) return false;
        const label = labels[key] || key.replace(/_/g, " ");
        return label.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="column-management-list">
            <div className="mb-3 px-1">
                <div className="icon-form">
                    <span className="form-icon">
                        <i className="ti ti-search" />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search columns..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Select All Checkbox */}
            <div className="mb-3 px-1">
                <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded flex-wrap">
                    <label className="checkboxs mb-0 d-flex align-items-center" style={{ cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={columnKeys.every((key) => visibleColumns[key] === true)}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                // Toggle all columns in columnKeys that don't match newValue
                                columnKeys.forEach((key) => {
                                    if (visibleColumns[key] !== newValue) {
                                        onColumnToggle(key);
                                    }
                                });
                            }}
                        />
                        <span className="checkmarks" />
                        <span className="ms-2 fw-semibold">Select All Columns</span>
                    </label>
                    <div className="ms-auto pt-1 pt-sm-0">
                        <span className="badge bg-soft-primary text-primary">
                            {columnKeys.filter(key => visibleColumns[key]).length} / {columnKeys.length} Selected
                        </span>
                    </div>
                </div>
            </div>

            <div style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "4px" }}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={columnKeys}
                        strategy={verticalListSortingStrategy}
                    >
                        {filteredKeys.length > 0 ? (
                            filteredKeys.map((key) => (
                                <SortableItem
                                    key={key}
                                    id={key}
                                    label={labels[key] || key.replace(/_/g, " ")}
                                    isVisible={visibleColumns[key] ?? false}
                                    onToggle={onColumnToggle}
                                    isDraggable={!fixedKeys.includes(key)}
                                />
                            ))
                        ) : (
                            <div className="text-center py-3 text-muted">
                                No columns found
                            </div>
                        )}
                    </SortableContext>
                </DndContext>
            </div>
        </div>
    );
};

export default ColumnManagement;
